import { useState } from "react";
import { Download } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { pixelLead } from "@/lib/pixel";
import { apiErrorMessage } from "@/lib/apiError";
import { leadFormSchema, type LeadFormValues } from "@/lib/leadSchema";
import leadService from "@/services/leadService";

/**
 * Lead magnet corporativo (HU-14): descarga del catálogo a cambio del email.
 *
 * Captura el email, entrega el catálogo vigente y marca el evento `Lead` para
 * retargeting. Sin catálogo cargado, avisa que aún no está disponible en vez de
 * prometer una descarga que no existe.
 */
export const LeadMagnet = () => {
  const { toast } = useToast();
  const [sentUrl, setSentUrl] = useState<string | null>(null);

  const { data: catalogStatus } = useQuery({
    queryKey: ["catalog-status"],
    queryFn: () => leadService.getCatalogStatus(),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LeadFormValues>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: { email: "", website: "" },
  });

  const onSubmit = async (values: LeadFormValues) => {
    // Honeypot: si un bot lo completó, no se envía nada (el server también filtra).
    if (values.website) return;

    try {
      const { catalogUrl } = await leadService.createLead({
        email: values.email,
        source: "catalogo",
      });

      pixelLead();

      if (catalogUrl) {
        setSentUrl(catalogUrl);
        toast({
          title: "¡Listo!",
          description: "Te enviamos el catálogo a tu correo.",
        });
      } else {
        toast({
          title: "Recibimos tu email",
          description: "Te hacemos llegar el catálogo en breve.",
        });
      }

      reset();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "No pudimos enviar tu email",
        description:
          apiErrorMessage(error) ??
          "Revisá tu conexión e intentá de nuevo, o escribinos por WhatsApp.",
      });
    }
  };

  if (catalogStatus && catalogStatus.available === false) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-primary/15 shadow-sm p-6 sm:p-8 text-center">
        <h3 className="font-playfair font-semibold text-xl text-brown-dark mb-2">
          Catálogo corporativo
        </h3>
        <p className="text-sm text-brown-light/90">
          El catálogo con los precios por volumen todavía no está disponible.
          Dejanos tu consulta con el formulario y te lo hacemos llegar.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-3xl border border-primary/15 shadow-sm p-6 sm:p-8 text-center">
      <h3 className="font-playfair font-semibold text-xl text-brown-dark mb-2">
        Descargá el catálogo corporativo
      </h3>
      <p className="text-sm text-brown-light/90 mb-5">
        Dejanos tu email y te enviamos el catálogo con los precios por volumen.
      </p>
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
      >
        <Input
          type="email"
          placeholder="tu@empresa.com"
          autoComplete="email"
          aria-label="Email para el catálogo"
          {...register("email")}
        />
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          className="hidden"
          aria-hidden="true"
          {...register("website")}
        />
        <Button
          type="submit"
          disabled={isSubmitting}
          className="bg-brown text-white hover:bg-brown-dark font-semibold px-6 py-4 rounded-full min-h-[52px] shrink-0"
        >
          <Download className="h-4 w-4 mr-2" />
          Descargar catálogo
        </Button>
      </form>
      {errors.email && (
        <p role="alert" className="text-sm text-destructive mt-3">
          {errors.email.message}
        </p>
      )}
      {sentUrl && (
        <p className="text-sm text-brown-light/90 mt-4">
          Te lo enviamos por correo. ¿No lo ves?{" "}
          <a
            href={sentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary-dark underline"
          >
            Descargalo ahora
          </a>
          .
        </p>
      )}
    </div>
  );
};

export default LeadMagnet;
